// components/doctor-dashboard/DoctorBusyManager.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  orderBy,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";

const DoctorBusyManager = ({ doctorId, showToast, tokens }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    startTime: "",
    endTime: "",
    notes: "",
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const loadBlocks = useCallback(async () => {
    if (!doctorId) return;
    setLoading(true);
    try {
      const qy = query(
        collection(db, "doctorBusy", doctorId, "blocks"),
        orderBy("date", "asc"),
        orderBy("startTime", "asc")
      );
      const snap = await getDocs(qy);
      const data = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setBlocks(data);
    } catch (err) {
      console.warn("[doctorBusy] load error", err);
      showToast?.("Failed to load busy blocks", "error");
    } finally {
      setLoading(false);
    }
  }, [doctorId, showToast]);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  const handleSave = async () => {
    if (!doctorId) return;
    const { date, startTime, endTime, notes } = form;
    if (!date || !startTime || !endTime) {
      showToast?.("Please fill date, start and end time", "error");
      return;
    }
    if (startTime >= endTime) {
      showToast?.("End time must be after start time", "error");
      return;
    }

    setSaving(true);
    try {
      await addDoc(collection(db, "doctorBusy", doctorId, "blocks"), {
        date,
        startTime,
        endTime,
        type: "busy",
        notes: notes?.trim() || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      showToast?.("Time blocked successfully", "success");
      setForm((prev) => ({
        ...prev,
        startTime: "",
        endTime: "",
        notes: "",
      }));
      await loadBlocks();
    } catch (err) {
      console.error("[doctorBusy] save error", err);
      showToast?.("Failed to block time", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (blockId) => {
    if (!doctorId || !blockId) return;
    try {
      await deleteDoc(doc(db, "doctorBusy", doctorId, "blocks", blockId));
      showToast?.("Block removed", "success");
      setBlocks((prev) => prev.filter((b) => b.id !== blockId));
    } catch (err) {
      console.error("[doctorBusy] delete error", err);
      showToast?.("Failed to remove block", "error");
    }
  };

  return (
    <div
      style={{
        padding: "16px",
        background: tokens.cardBg || "#fff",
        borderRadius: 12,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      {/* Mobile-first responsive styles */}
      <style jsx>{`
        .busy-form-grid {
          display: grid;
          gap: 14px;
          margin-bottom: 20px;
        }

        .busy-field {
          display: flex;
          flex-direction: column;
        }

        .busy-field label {
          font-size: 13px;
          font-weight: 600;
          color: ${tokens.textSubtle};
          margin-bottom: 6px;
        }

        .busy-field input {
          padding: 12px 14px;
          font-size: 16px; /* Prevents zoom on iOS */
          border-radius: 10px;
          border: 1.5px solid ${tokens.cardBorder};
          background: #fff;
          outline: none;
          transition: border 0.2s;
        }

        .busy-field input:focus {
          border-color: ${tokens.blue};
        }

        .busy-submit-btn {
          padding: 14px 20px;
          font-size: 16px;
          font-weight: 600;
          border-radius: 12px;
          background: ${tokens.blue};
          color: white;
          border: none;
          cursor: pointer;
          width: 100%;
          margin-top: 8px;
        }

        .busy-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .busy-block-item {
          padding: 14px;
          background: #f8fafc;
          border: 1px solid ${tokens.cardBorder};
          border-radius: 12px;
          font-size: 14px;
        }

        .busy-block-header {
          font-weight: 600;
          color: ${tokens.text};
          margin-bottom: 6px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }

        .busy-block-time {
          font-weight: 600;
          color: ${tokens.text};
        }

        .busy-block-notes {
          color: ${tokens.textSubtle};
          font-size: 13px;
          margin-top: 4px;
          word-break: break-word;
        }

        .busy-remove-btn {
          background: #fef2f2;
          color: #dc2626;
          border: none;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          min-width: 80px;
          margin-top: 10px;
        }

        @media (min-width: 640px) {
          .busy-form-grid {
            grid-template-columns: 1fr 1fr 1fr 2fr auto;
            align-items: end;
          }

          .busy-submit-btn {
            width: auto;
            margin-top: 0;
          }

          .busy-block-header {
            align-items: center;
          }

          .busy-remove-btn {
            margin-top: 0;
          }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: tokens.text, margin: 0 }}>
          Block Your Unavailable Time
        </h3>
        <p style={{ fontSize: 13, color: tokens.textSubtle, margin: "6px 0 0" }}>
          Prevent patients from booking during surgery, meetings, vacation, etc.
        </p>
      </div>

      {/* Form */}
      <div className="busy-form-grid">
        <div className="busy-field">
          <label>Date</label>
          <input
            type="date"
            value={form.date}
            onChange={(e) => handleChange("date", e.target.value)}
          />
        </div>

        <div className="busy-field">
          <label>Start Time</label>
          <input
            type="time"
            value={form.startTime}
            onChange={(e) => handleChange("startTime", e.target.value)}
          />
        </div>

        <div className="busy-field">
          <label>End Time</label>
          <input
            type="time"
            value={form.endTime}
            onChange={(e) => handleChange("endTime", e.target.value)}
          />
        </div>

        <div className="busy-field">
          <label>Notes (optional)</label>
          <input
            type="text"
            value={form.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            placeholder="e.g. Surgery, Annual leave"
          />
        </div>

        <button
          className="busy-submit-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Blocking..." : "Block Time"}
        </button>
      </div>

      {/* Blocked Times List */}
      <div>
        <h4 style={{ fontSize: 14, fontWeight: 600, color: tokens.textSubtle, margin: "20px 0 12px" }}>
          Upcoming Blocked Times
        </h4>

        {loading && (
          <div style={{ color: tokens.textSubtle, fontSize: 14, padding: "16px 0" }}>
            Loading blocked times...
          </div>
        )}

        {!loading && blocks.length === 0 && (
          <div style={{ color: tokens.textSubtle, fontSize: 14, padding: "16px 0", textAlign: "center" }}>
            No blocked times yet
          </div>
        )}

        {!loading && blocks.length > 0 && (
          <div style={{ display: "grid", gap: 12 }}>
            {blocks.map((b) => (
              <div key={b.id} className="busy-block-item">
                <div className="busy-block-header">
                  <div>
                    <div className="busy-block-time">
                      {new Date(b.date).toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      })}{" "}
                      • {b.startTime} – {b.endTime}
                    </div>
                    {b.notes && (
                      <div className="busy-block-notes">{b.notes}</div>
                    )}
                  </div>
                  <button
                    className="busy-remove-btn"
                    onClick={() => handleDelete(b.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorBusyManager;